import { categoryIdForStatusCategory } from '@atlassian/jira-common-constants/src/status-categories.tsx';
import type { UserValue } from '@atlassian/jira-issue-field-assignee/src/common/types.tsx';
import { getUserShape } from '@atlassian/jira-issue-field-assignee/src/common/utils.tsx';
import type {
	ParentShape,
	HierarchyShape,
} from '@atlassian/jira-issue-field-parent-switcher/src/common/types.tsx';
import {
	getParentShape,
	getHierarchyShape,
} from '@atlassian/jira-issue-field-parent-switcher/src/common/utils.tsx';
import type { PriorityValue } from '@atlassian/jira-issue-field-priority-chip/src/common/types.tsx';
import { BoardView } from '@atlassian/jira-platform-board-kit/src/common/constants/index.tsx';
import type { BoardType } from '@atlassian/jira-platform-board-kit/src/common/types.tsx';
import {
	HIERARCHY_LEVEL_ABOVE,
	type SUMMARY_TYPE,
	type ASSIGNEE_TYPE,
	type PARENT_TYPE,
	STORY_POINT_ESTIMATE_CF_TYPE,
	SUMMARY_TYPE as SYSTEM_SUMMARY_TYPE,
	TIME_ESTIMATE_TYPE as SYSTEM_TIME_ESTIMATE_TYPE,
	ASSIGNEE_TYPE as SYSTEM_ASSIGNEE_TYPE,
	PARENT_TYPE as SYSTEM_PARENT_TYPE,
	PRIORITY_TYPE as SYSTEM_PRIORITY_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import type {
	IssueStatusesById,
	IssueStatus,
} from '@atlassian/jira-software-board/src/view/board-container/board/card-container/card/status-field/types.tsx';
import { TimeEstimateKey } from '@atlassian/jira-providers-issue/src/model/issue-system-fields.tsx';
import type { Estimate } from '../../../../../model/issue/issue-types.tsx';
import type { PlatformCard } from '../../../../../services/issue/issue-data-transformer.tsx';
import type { StatusesState } from '../../../../../state/reducers/entities/statuses/types.tsx';

import {
	USER_FETCH_URL,
	SUMMARY_CONFIG_TITLE,
	STORY_POINT_CONFIG_TITLE,
	ORIGINAL_ESTIMATE_CONFIG_TITLE,
	ASSIGNEE_CONFIG_TITLE,
	PARENT_CONFIG_TITLE,
	PRIORITY_CONFIG_TITLE,
} from './constants.tsx';
import type { StateProps } from './types.tsx';

type IssueFieldValues =
	| {
			[SUMMARY_TYPE]?: string;
			[HIERARCHY_LEVEL_ABOVE]?: HierarchyShape | null;
			[PARENT_TYPE]?: ParentShape | null;
			[ASSIGNEE_TYPE]?: UserValue;
			[SYSTEM_PRIORITY_TYPE]?: PriorityValue;
	  }
	| {}; // for estimate fields

type IssueFieldsInput = Pick<StateProps, 'assignee' | 'parent'> & {
	estimateExtended: Estimate;
	issue: PlatformCard;
};

export const transformToIssueFieldValues = ({
	issue: { summary, priority },
	assignee,
	parent,
	estimateExtended,
}: IssueFieldsInput): IssueFieldValues => ({
	...{ summary },
	...(parent && {
		parent: getParentShape(
			String(parent.id),
			parent.key,
			parent.summary,
			{
				id: parent.issueType.id,
				name: parent.issueType.name,
				iconUrl: parent.issueType.iconUrl,
			},
			parent.color,
		),
		[HIERARCHY_LEVEL_ABOVE]: getHierarchyShape({
			id: parent.issueType.id,
			name: parent.issueType.name,
			iconUrl: parent.issueType.iconUrl,
		}),
	}),
	...(assignee && {
		assignee: getUserShape(assignee.id, assignee.displayName, assignee.avatarUrl || ''),
	}),
	...(estimateExtended &&
		estimateExtended.fieldId && {
			[estimateExtended.fieldId]: estimateExtended.value,
		}),
	...(priority && {
		[SYSTEM_PRIORITY_TYPE]: priority,
	}),
});
type IssueFieldConfigsInput = {
	estimateExtended: Estimate;
	isEditable: boolean;
	issue: PlatformCard;
};

export const transformToIssueFieldConfigValues = ({
	issue: { key, summary },
	estimateExtended,
	isEditable,
}: IssueFieldConfigsInput) => {
	const summaryConfig = {
		autoCompleteUrl: null,
		allowedValues: [],
		isEditable,
		isRequired: true,
		title: SUMMARY_CONFIG_TITLE,
		schema: {
			type: 'string',
			custom: null,
			customId: null,
			system: SYSTEM_SUMMARY_TYPE,
			renderer: 'jira-text-renderer',
		},
	};

	const storyPointConfig = {
		autoCompleteUrl: null,
		allowedValues: [],
		isEditable,
		isRequired: true,
		title: STORY_POINT_CONFIG_TITLE,
		schema: {
			type: 'number',
			custom: STORY_POINT_ESTIMATE_CF_TYPE,
			customId: estimateExtended?.fieldId
				? parseInt(estimateExtended.fieldId.replace('customfield_', ''), 10)
				: null,
			system: null,
			renderer: 'jira-text-renderer',
		},
	};

	const originalEstimateConfig = {
		autoCompleteUrl: null,
		allowedValues: [],
		isEditable,
		isRequired: true,
		title: ORIGINAL_ESTIMATE_CONFIG_TITLE,
		schema: {
			type: 'number',
			custom: null,
			customId: null,
			system: SYSTEM_TIME_ESTIMATE_TYPE,
			renderer: null,
		},
	};

	const assigneeConfig = {
		autoCompleteUrl: `${USER_FETCH_URL}?issueKey=${key}&query=`,
		allowedValues: [],
		isEditable,
		isRequired: true,
		title: ASSIGNEE_CONFIG_TITLE,
		schema: {
			type: 'user',
			custom: null,
			customId: null,
			system: SYSTEM_ASSIGNEE_TYPE,
			renderer: 'jira-text-renderer',
		},
	};

	const parentConfig = {
		autoCompleteUrl: null,
		allowedValues: [],
		isEditable,
		isRequired: true,
		title: PARENT_CONFIG_TITLE,
		schema: {
			type: 'array',
			custom: null,
			customId: null,
			system: SYSTEM_PARENT_TYPE,
			renderer: null,
		},
	};

	const priorityConfig = {
		autoCompleteUrl: null,
		allowedValues: [],
		isEditable,
		isRequired: false,
		title: PRIORITY_CONFIG_TITLE,
		schema: {
			type: 'priority',
			custom: null,
			customId: null,
			system: SYSTEM_PRIORITY_TYPE,
			renderer: 'null',
		},
	};

	return {
		...(summary && {
			summary: summaryConfig,
		}),
		parent: parentConfig,
		assignee: assigneeConfig,
		...(estimateExtended?.fieldId &&
			TimeEstimateKey === estimateExtended.fieldId && {
				[estimateExtended.fieldId]: originalEstimateConfig,
			}),
		...(estimateExtended?.fieldId &&
			TimeEstimateKey !== estimateExtended.fieldId && {
				[estimateExtended.fieldId]: storyPointConfig,
			}),
		priority: priorityConfig,
	};
};

export const getShouldShowLabels = (
	viewSettingsShowLabels: boolean,
	isCMPBoard: boolean,
): boolean => {
	// TMP
	if (!isCMPBoard) {
		return viewSettingsShowLabels;
	}

	// CMP
	return false; // disregard viewSettings.showLabel in CMP to avoid double labels // CMP now renders labels from custom fields
};

export const transformStatusesStateToIssueStatusesById = (
	statusesState: StatusesState,
): IssueStatusesById => {
	const issueStatusesById: IssueStatusesById = {};
	for (const [key, status] of Object.entries(statusesState)) {
		const categoryId = categoryIdForStatusCategory(status.category);

		const issueStatus: IssueStatus = {
			categoryId,
			id: String(status.id),
			name: status.name,
		};

		issueStatusesById[key] = issueStatus;
	}
	return issueStatusesById;
};

export type ShouldShowDueDateParams = {
	viewSettingsShowDueDate: boolean;
	isCMPBoard: boolean;
	boardType: BoardType;
};

export const shouldShowDueDate = ({
	viewSettingsShowDueDate,
	isCMPBoard,
	boardType,
}: ShouldShowDueDateParams): boolean => {
	if (!viewSettingsShowDueDate) {
		return false;
	}
	return !isCMPBoard || boardType === BoardView.JSW_BOARD;
};
